import { queryOptions, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { totalsByCurrency, type CurrencyTotal } from '../../platform/money/totals'
import { addMonths, currentOf, nextMonth, type YearMonth } from './monthParam'
import {
  localToday,
  OVERDUE_STATUSES,
  PAID_STATUSES,
  SKIPPED_STATUSES,
  UPCOMING_STATUSES,
  type LocalDate,
} from './recurringRules'
import { toBill, toEntry, toPeriod, type Bill, type Entry, type Period, type PeriodStatus } from './recurringWire'

export class RecurringError extends Error {
  readonly status: number | undefined
  constructor(cause: unknown, status?: number) {
    super('recurring-unavailable', { cause })
    this.name = 'RecurringError'
    this.status = status
  }
}

const isMissing = (e: unknown) => e instanceof RecurringError && e.status === 404

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Every request passes TanStack's `signal`, so a superseded month is really aborted. */
async function fetchBills(signal: AbortSignal): Promise<Bill[]> {
  const { data, error, response } = await api.GET('/api/v1/recurring', { signal })
  if (error !== undefined || data === undefined) throw new RecurringError(error, response.status)
  return data.recurring.map(toBill).filter((b) => !b.isRemoved)
}

async function fetchMonth(ym: YearMonth, signal: AbortSignal): Promise<Entry[]> {
  const { data, error, response } = await api.GET('/api/v1/recurring/calendar', {
    params: { query: { month: ym.month, year: ym.year } },
    signal,
  })
  if (error !== undefined || data === undefined) throw new RecurringError(error, response.status)
  return data.entries.map(toEntry)
}

async function fetchBill(id: string, signal: AbortSignal): Promise<Bill> {
  const { data, error, response } = await api.GET('/api/v1/recurring/{recurring_id}', {
    params: { path: { recurring_id: id } },
    signal,
  })
  if (error !== undefined || data === undefined) throw new RecurringError(error, response.status)
  return toBill(data)
}

export function billsQueryOptions() {
  return queryOptions({ queryKey: keys.recurring.list(), queryFn: ({ signal }) => fetchBills(signal) })
}

export function monthQueryOptions(ym: YearMonth) {
  return queryOptions({
    queryKey: keys.recurring.calendar(ym.month, ym.year),
    queryFn: ({ signal }) => fetchMonth(ym, signal),
  })
}

export type PeriodsResult = { periods: Period[]; truncated: boolean }

/** The backend caps a timeline at this many periods, so a full one may be cut short. */
const TIMELINE_CAP = 120
const PERIODS_MONTHS_BACK = 14

/** `today` minus `months` calendar months, the day clamped to the target month's length. */
function monthsBack(today: LocalDate, months: number): LocalDate {
  const { year, month } = addMonths(currentOf(today), -months)
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const day = Math.min(Number(today.slice(8, 10)), last)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${year}-${pad(month)}-${pad(day)}`
}

/** Shared by `usePeriods` and the composed `useQueries` the bills list will run. */
export function periodsQueryOptions(id: string, today: LocalDate) {
  return queryOptions({
    queryKey: [...keys.recurring.payments(id), { today }] as const,
    queryFn: async ({ signal }): Promise<PeriodsResult> => {
      const { data, error, response } = await api.GET('/api/v1/recurring/{recurring_id}/payments', {
        params: { path: { recurring_id: id }, query: { from_date: monthsBack(today, PERIODS_MONTHS_BACK) } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new RecurringError(error, response.status)
      return { periods: data.timeline.map(toPeriod), truncated: data.timeline.length >= TIMELINE_CAP }
    },
  })
}

export function useBills(): AsyncState<Bill[]> {
  const query = useQuery(billsQueryOptions())
  return fromQuery(query, {
    message: () => 'Your bills could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * The bill a sheet is open on, as the bills query holds it NOW. A sheet that
 * kept the `Bill` it was opened with would sit on a due date a refetch has
 * already refuted. A bill that has left the list (or a list not yet back)
 * falls back to the snapshot, so the sheet never vanishes under the user.
 */
export function liveBill(snapshot: Bill, bills: AsyncState<Bill[]>): Bill {
  if (bills.status !== 'success' && bills.status !== 'stale') return snapshot
  return bills.data.find((b) => b.id === snapshot.id) ?? snapshot
}

export function useMonth(ym: YearMonth): AsyncState<Entry[]> {
  const query = useQuery(monthQueryOptions(ym))
  return fromQuery(query, {
    message: () => 'This month could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

export type MoneyByCurrency = CurrencyTotal[]
/** `totals.length > 1` means the bucket mixes currencies, so no one figure exists. */
export type Bucket = { count: number; totals: MoneyByCurrency }

/**
 * P5, in one place: a paid entry is what was actually paid (a cash payment
 * carries its amount too), anything else is what was expected.
 */
export function entryAmountCents(e: Entry): number {
  return e.status === 'paid' ? (e.payment?.amountCents ?? e.expectedCents) : e.expectedCents
}

/** P2 status groups: defined in `recurringRules` beside `sectionOf`, re-exported for existing readers. */
export { OVERDUE_STATUSES, UPCOMING_STATUSES, SKIPPED_STATUSES, PAID_STATUSES }

function bucketOf(entries: Entry[]): Bucket {
  return { count: entries.length, totals: totalsByCurrency(entries, entryAmountCents, (e) => e.bill.currency) }
}

/** P2 sections, outflows only (D6): an inflow is income, never "due". */
export function bucketTotals(entries: Entry[]): { overdue: Bucket; upcoming: Bucket; skipped: Bucket; paid: Bucket } {
  const out = entries.filter((e) => !e.bill.isInflow)
  const by = (s: readonly PeriodStatus[]) => out.filter((e) => s.includes(e.status))
  return {
    overdue: bucketOf(by(OVERDUE_STATUSES)),
    upcoming: bucketOf(by(UPCOMING_STATUSES)),
    skipped: bucketOf(by(SKIPPED_STATUSES)),
    paid: bucketOf(by(PAID_STATUSES)),
  }
}

export type HeaderSummary = { activeCount: number; monthLabel: string; due: MoneyByCurrency }

/**
 * R1, D9: always NEXT calendar month, whatever month the screen is showing.
 * Needs its own calendar fetch, which is the same cache entry the month strip
 * uses when the user visits that month.
 */
export function useHeaderSummary(today: LocalDate): AsyncState<HeaderSummary> {
  const next = nextMonth(currentOf(today))
  const bills = useQuery(billsQueryOptions())
  const month = useQuery(monthQueryOptions(next))
  const data: HeaderSummary | undefined =
    bills.data && month.data
      ? {
          activeCount: bills.data.filter((b) => b.isActive).length,
          monthLabel: MONTH_NAMES[next.month - 1] ?? '',
          due: bucketTotalsDue(month.data),
        }
      : undefined
  return fromQuery(
    { data, isPending: bills.isPending || month.isPending, isError: bills.isError || month.isError, error: bills.error ?? month.error },
    {
      message: () => 'The summary could not be loaded.',
      recovery: {
        label: 'Try again',
        onAct: () => {
          void bills.refetch()
          void month.refetch()
        },
      },
    },
  )
}

/** Unpaid outflows: upcoming + missed + unknown, per currency. */
function bucketTotalsDue(entries: Entry[]): MoneyByCurrency {
  return totalsByCurrency(
    entries.filter((e) => !e.bill.isInflow && (OVERDUE_STATUSES.includes(e.status) || UPCOMING_STATUSES.includes(e.status))),
    entryAmountCents,
    (e) => e.bill.currency,
  )
}

export function useBill(id: string): AsyncState<Bill> & { removed: boolean } {
  const query = useQuery({
    queryKey: keys.recurring.detail(id),
    queryFn: ({ signal }) => fetchBill(id, signal),
    retry: (count, error) => !isMissing(error) && count < 1,
  })
  // Either signal: an `is_removed` bill, or a 404 — including a 404 on a
  // REFETCH, when TanStack keeps the old bill cached beside the error (the
  // bill was deleted on another device while this screen had it open).
  const removed = query.data?.isRemoved === true || (query.isError && isMissing(query.error))
  const state = fromQuery(query, {
    message: (e) => (isMissing(e) ? 'This bill no longer exists.' : 'This bill could not be loaded.'),
    ...(removed ? {} : { recovery: { label: 'Try again', onAct: () => void query.refetch() } }),
  })
  return { ...state, removed }
}

export function usePeriods(id: string, today: LocalDate = localToday()): AsyncState<PeriodsResult> {
  const query = useQuery(periodsQueryOptions(id, today))
  return fromQuery(query, {
    message: () => 'The payment history could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}
